import { useCallback, useRef } from "react";

import { useAllocationUpdateContext } from "../context/AllocationUpdateContext";
import { TIMING } from "../utils/allocation-constants";

type UpdateOptions = {
  rowId: number;
  immediate?: boolean; // for checkboxes - execute immediately
  debounceMs?: number; // for text inputs - debounce multiple rapid changes
  maxWaitMs?: number; // max time to wait before forcing update (throttle effect)
};

type UpdateFunction = () => Promise<void>;

export const useAllocationUpdate = ({ rowId }: { rowId: number }) => {
  const { isAnyRowUpdating, lockRow, unlockRow, queueUpdate } =
    useAllocationUpdateContext();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const maxWaitTimerRef = useRef<NodeJS.Timeout | null>(null);
  const updateCounterRef = useRef(0);

  const executeUpdate = useCallback(
    (fn: UpdateFunction, options: UpdateOptions = { rowId }) => {
      const {
        immediate = false,
        debounceMs = TIMING.DEBOUNCE_MS,
        maxWaitMs = TIMING.MAX_WAIT_MS,
      } = options;

      // immediately lock the row to disable checkboxes
      lockRow(rowId);

      // clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      const executeRequest = () => {
        const requestId = `${rowId}-${Date.now()}-${updateCounterRef.current++}`;

        // clear max wait timer since we're executing
        if (maxWaitTimerRef.current) {
          clearTimeout(maxWaitTimerRef.current);
          maxWaitTimerRef.current = null;
        }

        queueUpdate({
          id: requestId,
          rowId,
          execute: fn,
          onComplete: () => {
            unlockRow(rowId);
          },
          onError: () => {
            unlockRow(rowId);
          },
        });
      };

      if (immediate || debounceMs === 0) {
        // for checkboxes - execute immediately
        executeRequest();
      } else {
        // for text inputs - debounce with max wait (throttle effect)

        // set up max wait timer (throttle) if not already set
        if (!maxWaitTimerRef.current && maxWaitMs > 0) {
          maxWaitTimerRef.current = setTimeout(() => {
            executeRequest();
          }, maxWaitMs);
        }

        // set up debounce timer
        debounceTimerRef.current = setTimeout(() => {
          executeRequest();
        }, debounceMs);
      }
    },
    [rowId, lockRow, unlockRow, queueUpdate],
  );

  return {
    executeUpdate,
    isAnyRowUpdating,
  };
};
